import { useState, useRef, useEffect } from "react";
import Input from "../components/Input";
import Select from "../components/Select";
import Btn from "../components/Btn";
import Badge from "../components/Badge";
import StepBar from "../components/StepBar";
import { T } from "../constants/theme";
import { API_BASE_URL } from "../config";

export default function RegistrationForm({
  onNext,
  initialData,
  isEditing
}) {
  const [form, setForm] = useState({
    name: "",
    photo: null,
    photoPreview: null,
    grade: "",
    medium: "",
    schoolName: "",
    parentName: "",
    mobile: "",
    email: "",
    address: "",
    village: "",
    stateId: "",
    districtId: "",
    talukaId: "",

    schoolId: "",
    schoolName: "",
  });

  useEffect(() => {
    if (!initialData) return;

    setForm((prev) => ({
      ...prev,

      name: initialData.name || "",
      photo: initialData.photo || null,
      photoPreview: initialData.photoPreview || null,

      grade: initialData.grade || "",
      medium: initialData.medium || "",

      parentName: initialData.parentName || "",

      mobile: initialData.mobile || "",

      email: initialData.email || "",

      address: initialData.address || "",

      stateId:
        initialData.stateId?.toString() || "",

      districtId:
        initialData.districtId?.toString() || "",

      talukaId:
        initialData.talukaId?.toString() || "",

      schoolId:
        initialData.schoolId?.toString() || "",

      schoolName:
        initialData.schoolName || "",

      village: initialData.village || "",
    }));
  }, [initialData]);

  useEffect(() => {

    if (!initialData)
      return;

    async function hydrateDropdowns() {

      if (initialData.stateId) {
        await loadDistricts(
          initialData.stateId
        );
      }

      if (initialData.districtId) {
        await loadTalukas(
          initialData.districtId
        );
      }

      if (initialData.talukaId) {
        await loadSchools(
          initialData.talukaId
        );
      }
    }

    hydrateDropdowns();

  }, [initialData]);

  const [errors, setErrors] = useState({});
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const fileRef = useRef();
  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const [showAddSchool, setShowAddSchool] = useState(false);

  const [newSchoolName, setNewSchoolName] = useState("");

  const [schoolExists, setSchoolExists] = useState(null);

  const [addingSchool, setAddingSchool] = useState(false);

  const [successMessage, setSuccessMessage] = useState("");

  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    loadStates();
  }, []);

  useEffect(() => {
    if (!successMessage) return;

    const timer = setTimeout(() => {
      setSuccessMessage("");
    }, 4000);

    return () => clearTimeout(timer);

  }, [successMessage]);


  useEffect(() => {
    if (!errorMessage) return;

    const timer = setTimeout(() => {
      setErrorMessage("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [errorMessage]);


  const loadStates = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/school/states`
      );

      const data = await response.json();

      setStates(data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadDistricts = async (stateId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/school/districts/${stateId}`
      );

      const data = await response.json();



      setDistricts(data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadTalukas = async (districtId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/school/talukas/${districtId}`
      );

      const data = await response.json();

      setTalukas(data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadSchools = async (talukaId) => {
    if (!talukaId) {
      setSchools([]);
      return;
    }

    try {
      setLoadingSchools(true);

      const response = await fetch(
        `${API_BASE_URL}/api/registration/school-dropdown/${talukaId}`
      );

      const data = await response.json();

      setSchools(data);
    }
    catch (err) {
      console.error(err);
    }
    finally {
      setLoadingSchools(false);
    }
  };

  useEffect(() => {
    const checkSchool = async () => {
      if (
        newSchoolName.trim().length < 3
      ) {
        setSchoolExists(null);
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/api/school/school-exists?schoolName=${encodeURIComponent(
          newSchoolName
        )}&stateId=${form.stateId}&districtId=${form.districtId}&talukaId=${form.talukaId}`
      );

      const exists = await response.json();

      setSchoolExists(exists);
    };

    const timeout =
      setTimeout(checkSchool, 500);

    return () =>
      clearTimeout(timeout);

  }, [
    newSchoolName,
    form.stateId,
    form.districtId,
    form.talukaId
  ]);

  const handleAddSchool = async () => {
    try {
      if (!newSchoolName.trim()) {
        setErrorMessage("Please enter school name.");
        return;
      }

      if (schoolExists) {
        setErrorMessage(
          "School already exists in this taluka."
        );
        return;
      }

      setAddingSchool(true);

      const response = await fetch(
        `${API_BASE_URL}/api/school/add-school`,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json"
          },

          body: JSON.stringify({
            schoolName:
              newSchoolName.trim(),

            stateId: Number(
              form.stateId
            ),

            districtId: Number(
              form.districtId
            ),

            talukaId: Number(
              form.talukaId
            )
          })
        }
      );

      if (!response.ok) {
        const error =
          await response.text();

        throw new Error(error);
      }

      const school =
        await response.json();

      await loadSchools(
        form.talukaId
      );

      setForm((f) => ({
        ...f,

        schoolId: school.id,

        schoolName:
          school.schoolName
      }));

      setSuccessMessage(
        `School '${school.schoolName}' added successfully`
      );

      setShowAddSchool(false);

      setNewSchoolName("");

      setSchoolExists(null);
    }
    catch (err) {
      setErrorMessage(
        err.message || "Unable to add school."
      );
    }
    finally {
      setAddingSchool(false);
    }
  };

  const openSchoolPopup = () => {
    setNewSchoolName("");
    setSchoolExists(null);

    setShowAddSchool(true);
  };

  const handlePhoto = (e) => {
    const file = e.target.files[0];

    if (!file) return;
    const url = URL.createObjectURL(file);
    setForm((f) => ({ ...f, photo: file, photoPreview: url }));
  };

  const handleRegistration = async () => {
    if (!validate()) return;
    try {
      const formData = new FormData();
      formData.append("FullName", form.name);
      formData.append("Grade", form.grade);
      formData.append("Medium", form.medium);
      // backend expects SchoolId
      formData.append("SchoolId", form.schoolId);
      formData.append("ParentName", form.parentName);
      formData.append("Mobile", form.mobile);
      formData.append("Email", form.email || "");
      formData.append("Address", form.address);
      formData.append("Village", form.village);
      formData.append("StateId", form.stateId);
      formData.append("DistrictId", form.districtId);
      formData.append("TalukaId", form.talukaId);
      formData.append("Source", 1);

      if (form.photo) {
        formData.append("Photo", form.photo);
      }

      const registrationId =
        sessionStorage.getItem("registrationId");

      console.log({
        isEditing,
        registrationId,
        method: isEditing ? "PUT" : "POST",
        url: isEditing
          ? `${API_BASE_URL}/api/Registration/${registrationId}`
          : `${API_BASE_URL}/api/Registration`,
      });

      const response = await fetch(
        isEditing
          ? `${API_BASE_URL}/api/Registration/${registrationId}`
          : `${API_BASE_URL}/api/Registration`,
        {
          method: isEditing ? "PUT" : "POST",
          body: formData,
        }
      );

      const text = await response.text();

      const data = text ? JSON.parse(text) : null;

      if (isEditing) {
        onNext(form);
        return;
      }

      if (!response.ok) {
        throw new Error(
          data.message ||
          data.error ||
          "Registration failed"
        );
      }

      /*
        Response:
        {
          id,
          studentId,
          fullName,
          mobile
        }
      */

      if (!isEditing) {
        sessionStorage.setItem(
          "registrationId",
          data.id
        );

        sessionStorage.setItem(
          "studentId",
          data.studentId
        );

        sessionStorage.setItem(
          "studentName",
          data.fullName
        );
      }

      sessionStorage.setItem(
        "schoolName",
        form.schoolName
      );

      onNext(form);
    }
    catch (err) {
      setErrorMessage(
        err.message || "Registration failed."
      );

      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
  };


  const validate = () => {
    const e = {};

    if (!form.name.trim()) e.name = "Full name is required";

    if (!form.grade) e.grade = "Please select a class";

    if (!form.medium) e.medium = "Please select exam medium";

    if (!form.schoolId)
      e.school = "Please select a school";

    if (!form.parentName.trim())
      e.parentName = "Parent / representative name is required";

    if (!form.mobile.trim()) e.mobile = "Mobile number is required";
    else if (!/^[6-9]\d{9}$/.test(form.mobile))
      e.mobile = "Enter a valid 10-digit mobile number";

    if (!form.address.trim()) e.address = "Address is required";

    if (!form.village.trim())
      e.village = "Village is required";

    if (!form.stateId)
      e.state = "Please select state";

    if (!form.districtId)
      e.district = "Please select district";

    if (!form.talukaId)
      e.taluka = "Please select taluka";

    if (!form.schoolId)
      e.school = "Please select school";

    setErrors(e);

    return Object.keys(e).length === 0;
  };

  return (
    <div>
      <StepBar step={1} />

      <div style={{ marginBottom: 22 }}>
        <Badge>Step 1 of 5</Badge>

        {
          errorMessage && (
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #ef4444",
                color: "#991b1b",
                padding: "12px",
                borderRadius: "8px",
                marginBottom: "12px",
                fontSize: "14px"
              }}
            >
              ❌ {errorMessage}
            </div>
          )
        }

        {
          successMessage && (
            <div
              style={{
                background: "#ecfdf5",
                border: "1px solid #10b981",
                color: "#065f46",
                padding: "12px",
                borderRadius: "8px",
                marginBottom: "12px",
                fontSize: "14px"
              }}
            >
              ✅ {successMessage}
            </div>
          )
        }

        <h2
          style={{
            fontSize: 20,

            fontWeight: 800,

            color: T.indigo,

            margin: "8px 0 4px",
          }}
        >
          Student Registration
        </h2>

        <p style={{ fontSize: 13, color: T.gray600 }}>
          Please enter the required information carefully.
        </p>
      </div>

      {/* Section: Required Information */}

      <div
        style={{
          fontSize: 12,

          fontWeight: 700,

          color: T.gray600,

          letterSpacing: "0.06em",

          textTransform: "uppercase",

          marginBottom: 14,

          paddingBottom: 6,

          borderBottom: `1px solid ${T.gray100}`,
        }}
      >
        Required Information
      </div>

      {/* Photo Upload */}

      <div style={{ marginBottom: 20 }}>
        <label
          style={{
            display: "block",

            fontSize: 13,

            fontWeight: 600,

            color: T.gray800,

            marginBottom: 8,
          }}
        >
          Passport Size Photo <span style={{ color: T.saffron }}>*</span>
        </label>

        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            onClick={() => fileRef.current.click()}
            style={{
              width: 72,

              height: 72,

              borderRadius: 10,

              border: `2px dashed ${T.gray200}`,

              background: T.gray50,

              cursor: "pointer",

              overflow: "hidden",

              display: "flex",

              alignItems: "center",

              justifyContent: "center",

              flexShrink: 0,
            }}
          >
            {form.photoPreview ? (
              <img
                src={form.photoPreview}
                alt="preview"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <span style={{ fontSize: 24 }}>📷</span>
            )}
          </div>

          <div>
            <Btn
              onClick={() => fileRef.current.click()}
              variant="outline"
              small
            >
              Upload Photo
            </Btn>

            <p style={{ fontSize: 11, color: T.gray400, marginTop: 5 }}>
              JPG, JPEG, PNG, HEIC · Max 2MB
            </p>
          </div>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          onChange={handlePhoto}
          style={{ display: "none" }}
        />
      </div>

      <Input
        label="Full Name of Student"
        value={form.name}
        onChange={(v) => set("name", v)}
        placeholder="Enter student's full name"
        required
        error={errors.name}
      />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Select
          label="Class"
          value={form.grade}
          onChange={(v) => set("grade", v)}
          required
          error={errors.grade}
          options={[
            { value: "", label: "— Select Class —" },

            { value: "1", label: "Grade 1" },
            { value: "2", label: "Grade 2" },
            { value: "3", label: "Grade 3" },
            { value: "4", label: "Grade 4" },
            { value: "5", label: "Grade 5" },
            { value: "6", label: "Grade 6" },
            { value: "7", label: "Grade 7" },
            { value: "8", label: "Grade 8" },
          ]}
        />

        <Select
          label="Exam Medium"
          value={form.medium}
          onChange={(v) => set("medium", v)}
          required
          error={errors.medium}
          options={[
            { value: "", label: "— Select —" },

            { value: "Marathi", label: "मराठी (Marathi)" },
            { value: "Semi-English", label: "Semi-English" },
            { value: "English", label: "English" },
            { value: "CBSE", label: "CBSE" },
          ]}
        />
      </div>
      <Input
        label="Parent / Teacher / Representative Name"
        value={form.parentName}
        onChange={(v) => set("parentName", v)}
        placeholder="Enter parent or guardian's name"
        required
        error={errors.parentName}
      />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Input
          label="Mobile Number"
          type="tel"
          value={form.mobile}
          onChange={(v) => set("mobile", v)}
          placeholder="10-digit mobile number"
          required
          error={errors.mobile}
        />

        <Input
          label="Email Address"
          type="email"
          value={form.email}
          onChange={(v) => set("email", v)}
          placeholder="optional@email.com"
        />
      </div>

      {/* Section: Address Information */}

      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: T.gray600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          margin: "22px 0 14px",
          paddingBottom: 6,
          borderBottom: `1px solid ${T.gray100}`,
        }}
      >
        Address Information
      </div>

      <div style={{ marginBottom: 18 }}>
        <label
          style={{
            display: "block",
            fontSize: 13,
            fontWeight: 600,
            color: T.gray800,
            marginBottom: 5,
          }}
        >
          Complete Address <span style={{ color: T.saffron }}>*</span>
        </label>

        <textarea
          value={form.address}
          onChange={(e) => set("address", e.target.value)}
          placeholder="House No., Street, Area..."
          rows={2}
          style={{
            width: "100%",
            padding: "10px 13px",
            borderRadius: 8,
            border: `1.5px solid ${errors.address ? T.red : T.gray200}`,
            fontSize: 14,
            color: T.gray800,
            outline: "none",
            resize: "vertical",
            boxSizing: "border-box",
            fontFamily: "inherit",
          }}
        />

        {errors.address && (
          <p style={{ fontSize: 12, color: T.red, marginTop: 4 }}>
            ⚠ {errors.address}
          </p>
        )}
      </div>

      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}
      >
        <Input
          label="Village"
          value={form.village}
          onChange={(v) => set("village", v)}
          placeholder="Village"
          required
          error={errors.village}
        />

        <Select
          label="State"
          value={form.stateId}
          onChange={(v) => {
            setForm((f) => ({
              ...f,
              stateId: v,
              districtId: "",
              talukaId: "",
              schoolId: ""
            }));

            setDistricts([]);
            setTalukas([]);
            setSchools([]);

            loadDistricts(v);
          }}
          options={[
            { value: "", label: "Select State" },

            ...states.map((x) => ({
              value: x.id,
              label: x.name
            }))
          ]}
        />

        <Select
          label="District"
          value={form.districtId}
          onChange={(v) => {
            setForm((f) => ({
              ...f,
              districtId: v,
              talukaId: "",
              schoolId: ""
            }));

            setTalukas([]);
            setSchools([]);

            loadTalukas(v);
          }}
          options={[
            {
              value: "",
              label: "Select District"
            },

            ...districts.map((x) => ({
              value: x.id,
              label: x.name
            }))
          ]}
        />
      </div>

      <Select
        label="Taluka"
        value={form.talukaId}
        onChange={(v) => {
          setForm((f) => ({
            ...f,
            talukaId: v,
            schoolId: ""
          }));

          setSchools([]);

          loadSchools(v);
        }}
        options={[
          { value: "", label: "Select Taluka" },

          ...talukas.map((x) => ({
            value: x.id,
            label: x.name
          }))
        ]}
      />

      <Select
        label="School"
        value={form.schoolId}
        onChange={(v) => {
          const selectedSchool =
            schools.find(
              (s) => String(s.id) === String(v)
            );

          setForm((f) => ({
            ...f,
            schoolId: v,
            schoolName:
              selectedSchool?.schoolName || ""
          }));
        }}
        options={[
          {
            value: "",
            label: "Select School",
          },

          ...schools.map((x) => ({
            value: x.id,
            label: x.schoolName,
          })),
        ]}
      />


      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <div />

        <button
          type="button"
          disabled={
            !form.stateId ||
            !form.districtId ||
            !form.talukaId
          }
          onClick={openSchoolPopup}
          style={{
            border: "none",
            background: "transparent",
            color:
              !form.stateId ||
                !form.districtId ||
                !form.talukaId
                ? "#9ca3af"
                : "#2563eb",
            cursor:
              !form.stateId ||
                !form.districtId ||
                !form.talukaId
                ? "not-allowed"
                : "pointer",
            fontWeight: 600
          }}
        >
          + Add School
        </button>
      </div>

      <div style={{ marginTop: 8 }}>
        <Btn onClick={handleRegistration} full>
          Continue →
        </Btn>
      </div>

      {
        showAddSchool && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999
            }}
          >
            <div
              style={{
                width: 550,
                maxWidth: "95%",
                background: "#fff",
                borderRadius: 12,
                padding: 24,
                boxShadow:
                  "0 20px 40px rgba(0,0,0,0.15)"
              }}
            >
              <h3
                style={{
                  marginTop: 0,
                  marginBottom: 20
                }}
              >
                Add New School
              </h3>

              <Input
                label="School Name"
                value={newSchoolName}
                onChange={setNewSchoolName}
                placeholder="Enter school name"
              />

              {
                schoolExists === true && (
                  <p
                    style={{
                      color: "red",
                      fontSize: 13
                    }}
                  >
                    ⚠ School already exists in this
                    Taluka. Please search and select it.
                  </p>
                )
              }

              {
                schoolExists === false &&
                newSchoolName.trim().length >= 3 && (
                  <p
                    style={{
                      color: "green",
                      fontSize: 13
                    }}
                  >
                    ✓ School not found. You can add it.
                  </p>
                )
              }

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 10,
                  marginTop: 25
                }}
              >
                <Btn
                  variant="outline"
                  onClick={() =>
                    setShowAddSchool(false)
                  }
                >
                  Cancel
                </Btn>

                <Btn
                  onClick={handleAddSchool}
                  disabled={
                    addingSchool ||
                    !newSchoolName.trim() ||
                    schoolExists === true
                  }
                >
                  {
                    addingSchool
                      ? "Adding..."
                      : "Add School"
                  }
                </Btn>
              </div>
            </div>
          </div>
        )
      }
    </div>
  );
};